import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  VersionsTable, DisclosuresTable, DatapointsTable,
  ApplicabilityRulesTable, ValidationRulesTable,
} from "@/components/admin/StandardTables";
import { toast } from "sonner";

export default function AdminStandard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/standard")
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(formatApiError(e));
        toast.error("Accesso non consentito", { description: formatApiError(e) });
      });
  }, []);

  if (!user?.is_superadmin) {
    return (
      <div className="max-w-xl" data-testid="admin-forbidden">
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-600">
          Sezione riservata al Super Admin della piattaforma.
        </CardContent></Card>
      </div>
    );
  }

  if (error) {
    return <p role="alert" className="text-sm text-red-700" data-testid="admin-standard-error">{error}</p>;
  }
  if (!data) {
    return <p className="text-sm text-slate-600" data-testid="admin-standard-loading">Caricamento in corso…</p>;
  }

  return (
    <div className="space-y-6" data-testid="admin-standard-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-green-700" aria-hidden="true" />
          Standard Management
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Gestione delle versioni dello standard VSME, delle disclosure, dei datapoint e delle regole normative.
          Le nuove versioni potranno essere aggiunte senza alterare i report storici (version lock sui periodi).
        </p>
      </div>

      <Tabs defaultValue="versions">
        <TabsList data-testid="admin-standard-tabs">
          <TabsTrigger value="versions" data-testid="tab-versions">Versioni</TabsTrigger>
          <TabsTrigger value="disclosures" data-testid="tab-disclosures">Disclosure</TabsTrigger>
          <TabsTrigger value="datapoints" data-testid="tab-datapoints">Datapoint</TabsTrigger>
          <TabsTrigger value="applicability" data-testid="tab-applicability">Regole applicabilità</TabsTrigger>
          <TabsTrigger value="validation" data-testid="tab-validation">Regole validazione</TabsTrigger>
        </TabsList>

        <TabsContent value="versions"><VersionsTable versions={data.versions} /></TabsContent>
        <TabsContent value="disclosures"><DisclosuresTable disclosures={data.disclosures} /></TabsContent>
        <TabsContent value="datapoints"><DatapointsTable datapoints={data.datapoints} /></TabsContent>
        <TabsContent value="applicability"><ApplicabilityRulesTable rules={data.applicability_rules} /></TabsContent>
        <TabsContent value="validation"><ValidationRulesTable rules={data.validation_rules} /></TabsContent>
      </Tabs>
    </div>
  );
}
